import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ApiError } from '../api';
import { dayLabel } from '../dates';
import { firstName, length, money } from './format';
import { act, noteRef, useOutbox, usePortalLoad } from './offline';
import { ChevronIcon } from './PortalIcons';
import { Actions, LoadFailure, OfficeCall, SavedNote } from './shared';

interface Task {
  id: number | null;
  title: string;
  group?: string;
  description: string;
  done: boolean;
}
interface Note {
  id: number | string;
  body: string;
  created_at: string;
  waiting?: boolean;
}
interface Visit {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  minutes: number;
  status: string;
  customer_name: string;
  address: { line1: string; line2: string; locality: string; region: string; postcode: string };
  access_notes: string;
  access_notes_later: boolean;
  is_lead: boolean;
  accepted: boolean;
  changed: boolean;
  team: string[];
  pay: string | null;
  can_tick: boolean;
  can_complete: boolean;
  checklist: Task[];
  notes: Note[];
}

const STATUS: Record<string, string> = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  missed: 'Missed',
};

const mapsUrl = (where: string) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(where)}`;

interface Booked {
  id: number;
  date: string;
  start_time: string;
  locality: string;
  customer_name: string;
  status: string;
}

/** Where to go after finishing a visit: the next one booked, from the schedule this phone has. */
function NextVisit(props: { after: number }) {
  const { data } = usePortalLoad<{ today: string; items?: Booked[] }>('/api/portal/schedule');
  if (!data) return null;
  const next = (data.items ?? [])
    .filter((i) => i.id !== props.after && i.status === 'scheduled' && i.date >= data.today)
    .sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`))[0];
  return next ? (
    <p>
      Next:{' '}
      <Link to={`/visits/${next.id}`} state={{ from: '/' }}>
        {dayLabel(next.date)}, {next.start_time} · {firstName(next.customer_name)}, {next.locality}
      </Link>
    </p>
  ) : (
    <p>Nothing else booked yet.</p>
  );
}

/** Where Back goes, named: the screen the visit was opened from. */
const BACK: Record<string, string> = { '/': 'Today', '/week': 'Upcoming' };

/**
 * One visit. On a phone it is its own screen, ordered the way the day goes: where (and how
 * to get in) until the visit starts, then the tasks first; notes last, with the one thing to
 * do next pinned to the bottom. On a wide screen it is `embedded` beside the schedule,
 * without its own Back.
 */
export function PortalVisit(props: { id?: number; embedded?: boolean }) {
  const params = useParams();
  const id = props.id ?? params.id;
  const from = (useLocation().state as { from?: string } | null)?.from;
  const back = from && BACK[from] ? from : '/';
  const load = usePortalLoad<Visit>(`/api/portal/visits/${id}`);
  const { data, setData } = load;
  const [message, setMessage] = useState('');
  const [finished, setFinished] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /** The label of what is being sent, while its button waits for an answer. */
  const [sending, setSending] = useState('');
  const [note, setNote] = useState('');
  const [opened] = useState(() => Date.now());
  const Title = props.embedded ? 'h2' : 'h1';
  // Once the phone has sent what was waiting, "Saved on this phone" no longer applies.
  const { synced } = useOutbox();
  useEffect(() => setMessage(''), [synced]);
  // Focus follows the completion question: to it as it opens (so it is read out), back to
  // the button if they say not yet, and to the result once it is done.
  const question = useRef<HTMLElement>(null);
  const finish = useRef<HTMLButtonElement>(null);
  const result = useRef<HTMLDivElement>(null);
  const asked = useRef(false);
  useEffect(() => {
    if (confirming) {
      asked.current = true;
      question.current?.focus();
    } else if (asked.current) {
      asked.current = false;
      finish.current?.focus();
    }
  }, [confirming]);
  useEffect(() => {
    if (finished) result.current?.focus();
  }, [finished]);

  const backLink = !props.embedded && (
    <p>
      <Link to={back} className="p-back">
        <ChevronIcon back />
        Back to {BACK[back]}
      </Link>
    </p>
  );

  if (!data) {
    return (
      <div className="p-visit-page">
        {backLink}
        <Title className="p-visit-title">Visit</Title>
        {load.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <LoadFailure
            what="this visit"
            load={load}
            notFound="This visit is not on your schedule any more. The office may have moved or cancelled it."
          />
        )}
      </div>
    );
  }
  const v = data;

  /** Run an action: a refusal is shown, and a queued action is told apart from a sent one. */
  async function run(
    label: string,
    url: string,
    body?: unknown,
  ): Promise<'done' | 'queued' | 'refused'> {
    setMessage('');
    setFinished(false);
    try {
      const r = await act(label, 'POST', url, body);
      if (r === 'queued')
        setMessage('Saved on this phone. It will be sent when you have a signal.');
      return r;
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : 'Something went wrong.');
      return 'refused';
    }
  }

  /** For a button that waits: pressed once, it says "Sending…" until the answer comes or it is saved. */
  async function send(label: string, url: string, body?: unknown) {
    setSending(label);
    try {
      return await run(label, url, body);
    } finally {
      setSending('');
    }
  }

  async function accept() {
    if ((await send('Confirm visit', `/api/portal/visits/${v.id}/accept`)) !== 'refused') {
      setData({ ...v, accepted: true });
    }
  }

  async function tick(task: Task, done: boolean) {
    if (task.id === null) return;
    const before = v;
    setData({ ...v, checklist: v.checklist.map((t) => (t.id === task.id ? { ...t, done } : t)) });
    const r = await run(`Tick "${task.title}"`, `/api/portal/visits/${v.id}/tasks/${task.id}`, {
      done,
    });
    if (r === 'refused') setData(before);
  }

  async function complete() {
    setConfirming(false);
    const r = await send('Complete visit', `/api/portal/visits/${v.id}/complete`);
    if (r !== 'refused') {
      setData({ ...v, status: 'completed', can_complete: false, can_tick: false });
      if (r === 'done') setFinished(true);
    }
  }

  async function sendNote(e: FormEvent) {
    e.preventDefault();
    const body = note.trim();
    if (!body) return;
    const r = await send('Send note', `/api/portal/visits/${v.id}/notes`, {
      body,
      client_ref: noteRef(),
    });
    if (r !== 'refused') {
      setData({
        ...v,
        notes: [
          ...v.notes,
          {
            id: `local-${v.notes.length}`,
            body,
            created_at: new Date().toISOString(),
            waiting: r === 'queued',
          },
        ],
      });
      setNote('');
    }
  }

  const a = v.address;
  const where = [a.line1, a.line2, a.locality, a.region, a.postcode].filter(Boolean).join(', ');
  const preview = v.checklist.filter((t) => t.id === null);
  const tasks = v.checklist.filter((t) => t.id !== null);
  const ticked = tasks.filter((t) => t.done).length;
  const open = v.status === 'scheduled';
  const needsConfirming = open && !v.accepted;
  const canFinish = open && v.is_lead && v.can_complete;

  // The one thing to do next, pinned where a thumb can reach it. Confirming comes first:
  // until the visit is confirmed, Confirm stands where Mark complete will be.
  const primary = needsConfirming ? (
    <button type="button" className="p-big" disabled={sending !== ''} onClick={() => void accept()}>
      {sending === 'Confirm visit' ? 'Sending…' : 'Confirm I can do this visit'}
    </button>
  ) : canFinish ? (
    <button
      type="button"
      className="p-big"
      ref={finish}
      disabled={sending !== ''}
      onClick={() => setConfirming(true)}
    >
      {sending === 'Complete visit' ? 'Sending…' : 'Mark visit complete'}
    </button>
  ) : open && where ? (
    <a className="button p-big" href={mapsUrl(where)} target="_blank" rel="noreferrer">
      Directions<span className="sr-only"> (opens maps)</span>
    </a>
  ) : null;

  // Until the visit starts, finding the place and getting in come first; then the tasks.
  // Decided when the page opens, so the cards never swap under a thumb.
  const whereFirst = open && opened < new Date(`${v.date}T${v.start_time}`).getTime();

  const tasksCard = (
    <section aria-labelledby={`tasks-h-${v.id}`} className="card">
      <h2 id={`tasks-h-${v.id}`}>
        Tasks
        {tasks.length > 0 && (
          <span className="p-count">
            {ticked} of {tasks.length} done
          </span>
        )}
      </h2>
      {v.checklist.length === 0 && <p className="muted">No tasks for this visit.</p>}
      {tasks.length > 0 && (
        <Actions>
          <ul className="p-checks">
            {tasks.map((t) => (
              <li key={t.id}>
                <label className="check p-check">
                  <input
                    type="checkbox"
                    checked={t.done}
                    disabled={!v.can_tick}
                    onChange={(e) => void tick(t, e.target.checked)}
                  />
                  <span>
                    {t.title}
                    {t.description && <small className="muted"> {t.description}</small>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </Actions>
      )}
      {preview.length > 0 && (
        <>
          <p className="muted">You can tick these on the day of the visit.</p>
          <ul>
            {preview.map((t) => (
              <li key={t.title}>{t.title}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );

  const whereCard = (
    <section aria-labelledby={`where-h-${v.id}`} className="card">
      <h2 id={`where-h-${v.id}`}>Where</h2>
      <p>{where || 'No address on file.'}</p>
      {where && (
        <a href={mapsUrl(where)} target="_blank" rel="noreferrer">
          Open in maps
        </a>
      )}
      {v.access_notes && (
        <div className="p-access">
          <strong>Access notes (visible today only)</strong>
          <p className="p-pre">{v.access_notes}</p>
        </div>
      )}
      {!v.access_notes && v.access_notes_later && (
        <p className="muted">Access notes will show here on the day of the visit.</p>
      )}
    </section>
  );

  return (
    <div className="p-visit-page">
      {backLink}
      <Title className="p-visit-title">{v.customer_name}</Title>
      <p className="p-when">
        <strong>{dayLabel(v.date)}</strong>, {v.start_time}–{v.end_time} ({length(v.minutes)}){' '}
        {needsConfirming ? (
          <em className="p-status-tag is-unconfirmed">Needs confirming</em>
        ) : (
          <em className={`p-status-tag is-${v.status}`}>{STATUS[v.status] ?? v.status}</em>
        )}
      </p>
      <SavedNote load={load} extra="Changes you make will be sent later." />
      {v.changed && (
        <p role="status" className="notice">
          The office changed this visit since you last looked. Check the time and details.
        </p>
      )}
      {v.accepted && open && <p className="muted p-confirmed">You confirmed this visit.</p>}

      {whereFirst && whereCard}
      {tasksCard}
      {!whereFirst && whereCard}

      {(v.team.length > 0 || v.pay) && (
        <section aria-labelledby={`team-h-${v.id}`} className="card">
          <h2 id={`team-h-${v.id}`}>Team and pay</h2>
          {v.team.length > 0 && <p>Working with {v.team.join(', ')}.</p>}
          {v.pay && <p>Your pay for this visit: {money(v.pay)} plus GST where it applies.</p>}
        </section>
      )}

      <details className="card p-notes-card">
        <summary>
          Notes for the office
          {v.notes.length > 0 && <span className="p-count">{v.notes.length}</span>}
        </summary>
        {v.notes.length > 0 && (
          <ul className="p-notes">
            {v.notes.map((n) => (
              <li key={n.id}>
                {n.body}
                {n.waiting && <em className="muted"> (waiting to send)</em>}
              </li>
            ))}
          </ul>
        )}
        <Actions>
          <form onSubmit={sendNote} className="stack">
            <label>
              Write a note
              <textarea value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
            </label>
            <button type="submit" className="quiet" disabled={sending !== '' || note.trim() === ''}>
              {sending === 'Send note' ? 'Sending…' : 'Send note'}
            </button>
          </form>
        </Actions>
      </details>

      {open && v.is_lead && !v.can_complete && (
        <p className="muted">
          You can mark this visit complete from {v.start_time} on {dayLabel(v.date)}.
        </p>
      )}

      <div className="p-actionbar">
        {message && (
          <p role="status" className="notice">
            {message}
          </p>
        )}
        {finished && (
          <div role="status" className="p-done" ref={result} tabIndex={-1}>
            <p>
              <strong>Visit complete.</strong> The office can see it now.
            </p>
            <NextVisit after={v.id} />
          </div>
        )}
        {confirming ? (
          <div role="alertdialog" aria-labelledby={`sure-h-${v.id}`} className="p-sure">
            <strong id={`sure-h-${v.id}`} ref={question} tabIndex={-1}>
              {tasks.length > 0
                ? `Mark complete? ${ticked} of ${tasks.length} tasks are ticked.`
                : 'Mark complete?'}
            </strong>
            <div className="p-actionbar-row">
              <button type="button" className="p-big" onClick={() => void complete()}>
                Yes, it is complete
              </button>
              <button type="button" className="quiet p-big" onClick={() => setConfirming(false)}>
                Not yet
              </button>
            </div>
          </div>
        ) : (
          <Actions>
            <div className="p-actionbar-row">
              {primary}
              <OfficeCall className="p-big" />
            </div>
          </Actions>
        )}
      </div>
    </div>
  );
}
